/**
 * MobilePrompt Component
 *
 * Desktop-only invitation to open TIL on a phone: a QR code (encoding the
 * current origin) anchored in the empty side gutter that appears because the
 * app column is capped at 700px. Fixed positioning keeps it out of the content
 * flow, so it can never shift or overlap app content; the stylesheet hides it
 * whenever the gutter cannot fit it.
 */

import { useEffect, useState } from 'react';
import { lazy, Suspense } from 'react';
import { X } from '@/components/ui/icons';
import styles from './mobile-prompt.module.less';

// qrcode.react is ~46ms of module init and is only ever used by a desktop-gutter
// prompt, but it used to sit in the root chunk because __root.tsx imports this
// component. Dynamic import keeps it out of every page's initial chunk; the
// canShow gate below means a phone never even requests it.
const QRCodeSVG = lazy(() => import('qrcode.react').then((m) => ({ default: m.QRCodeSVG })));

// Mirrors @mobile-prompt-min-viewport in mobile-prompt.module.less:
// 700px app column + 2 * (186 card + 16 gap + 24 margin) = 1152px, plus the
// 480px height floor. Below this the stylesheet hides the card entirely, so
// rendering it would only cost work.
const CAN_SHOW_QUERY = '(min-width: 1152px) and (min-height: 480px)';

// Session-scoped on purpose: clearing when the tab closes lets the prompt
// return next session (unlike PWAUpdatePrompt, which persists its dismissal).
const DISMISS_KEY = 'til.mobile-prompt.dismissed';

function readSessionDismissed(): boolean {
  try {
    return sessionStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

export function MobilePrompt() {
  const [qrUrl, setQrUrl] = useState('');
  const [dismissed, setDismissed] = useState(false);
  // Starts false so SSR and the first client paint agree, and so a phone never
  // mounts the card (and never pulls the QR chunk) even though only CSS used to
  // hide it.
  const [canShow, setCanShow] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(CAN_SHOW_QUERY);
    const apply = () => setCanShow(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  // Read storage post-mount so SSR markup is deterministic and an in-session
  // dismissal can never cause a hydration mismatch.
  useEffect(() => {
    // The QR must encode a URL the phone can actually open in the current
    // environment. Staging and production encode their own origin; local dev
    // runs on *.til.test, which phones cannot resolve, so it points at the
    // staging deployment (local already shares staging data).
    const DEV_QR_URL: Partial<Record<string, string>> = { development: 'https://stg-app.til.sg' };
    setQrUrl(DEV_QR_URL[import.meta.env.VITE_APP_ENV ?? ''] ?? window.location.origin);
    setDismissed(readSessionDismissed());
  }, []);

  if (!qrUrl || dismissed || !canShow) return null;

  const dismiss = () => {
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      // Blocked storage must not keep the prompt on screen.
    }
    setDismissed(true);
  };

  return (
    <aside className={styles.mobilePrompt} aria-label="Open TIL on your phone">
      <button
        type="button"
        className={styles.dismiss}
        aria-label="Dismiss the mobile prompt"
        onClick={dismiss}
      >
        <X />
      </button>
      {/* QRs must stay max-contrast to scan, so they ignore the theme. */}
      <div className={styles.qrTile}>
        <Suspense fallback={<div style={{ width: 140, height: 140 }} aria-hidden="true" />}>
          <QRCodeSVG value={qrUrl} size={140} marginSize={2} />
        </Suspense>
      </div>
      <p className={styles.title}>Open TIL on your phone</p>
      <p className={styles.body}>
        Scan to take <span className={styles.accent}>the dance floor</span> with you.
      </p>
    </aside>
  );
}
